import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { Button } from "../ui/button.jsx";
import { Progress } from "../ui/progress.jsx";
import { 
  Building2, 
  Calendar, 
  User, 
  Users, 
  Clock,
  ChevronDown,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  EditIcon,
  Target,
  TrendingUp,
  Zap,
  Activity,
  Award,
  Timer,
  AlertTriangle,
  Star,
  BarChart3,
  PieChart,
  LineChart,
  Sparkles,
  Lightbulb,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar as CalendarIcon,
  Briefcase,
  DollarSign,
  TrendingDown
} from 'lucide-react';
import { format, differenceInDays, parseISO, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils/index.ts';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.jsx";

const STATUS_CONFIG = {
  planning: {
    label: 'Planejamento',
    icon: Lightbulb,
    gradient: 'from-purple-500 to-indigo-600',
    bgGradient: 'from-purple-50 to-indigo-50',
    color: 'text-purple-700',
    progress: 15
  },
  in_progress: {
    label: 'Em Andamento',
    icon: Zap,
    gradient: 'from-blue-500 to-cyan-600',
    bgGradient: 'from-blue-50 to-cyan-50',
    color: 'text-blue-700',
    progress: 50
  },
  paused: {
    label: 'Pausado',
    icon: PauseCircle,
    gradient: 'from-orange-500 to-red-600',
    bgGradient: 'from-orange-50 to-red-50',
    color: 'text-orange-700',
    progress: 25
  },
  completed: {
    label: 'Concluído',
    icon: CheckCircle,
    gradient: 'from-green-500 to-emerald-600',
    bgGradient: 'from-green-50 to-emerald-50',
    color: 'text-green-700',
    progress: 100
  }
};

export default function ProjectOverview({ project, company, manager, teamMembers, onStatusChange, tasks = [] }) {
  const [projectStats, setProjectStats] = useState(null);

  useEffect(() => {
    if (project && tasks) {
      calculateProjectStats();
    }
  }, [project, tasks]);

  const calculateProjectStats = () => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'done').length;
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
    const todoTasks = tasks.filter(t => t.status === 'todo').length;
    const reviewTasks = tasks.filter(t => t.status === 'review').length;
    
    const highPriorityTasks = tasks.filter(t => t.priority === 'high').length;
    const overdueTasks = tasks.filter(t => {
      if (!t.due_date) return false;
      return new Date(t.due_date) < new Date();
    }).length;

    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    // Calcular dias restantes
    let daysRemaining = null;
    let isOverdue = false;
    if (project.end_date) {
      const endDate = new Date(project.end_date);
      const today = new Date();
      daysRemaining = differenceInDays(endDate, today);
      isOverdue = daysRemaining < 0;
    }

    // Calcular duração total do projeto
    let totalDays = null;
    if (project.start_date && project.end_date) {
      totalDays = differenceInDays(new Date(project.end_date), new Date(project.start_date));
    }

    setProjectStats({
      totalTasks,
      completedTasks,
      inProgressTasks,
      todoTasks,
      reviewTasks,
      highPriorityTasks,
      overdueTasks,
      progress,
      daysRemaining,
      isOverdue,
      totalDays
    });
  };

  if (!project) return null;

  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;

  const handleStatusChange = (status) => {
    onStatusChange(status);
  };

  const getTeamMemberInitials = (member) => {
    const name = member.name || member.full_name || 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <div className="space-y-8">
      {/* Header Principal com Status */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative overflow-hidden rounded-3xl p-8 bg-gradient-to-br ${statusConfig.bgGradient} border border-white/20 shadow-2xl`}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <motion.div
                  className={`p-3 rounded-2xl bg-gradient-to-r ${statusConfig.gradient} shadow-lg`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <statusConfig.icon className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-1">{project.name}</h1>
                  <Badge className={`${statusConfig.color} bg-white/80 border-0 shadow-md text-sm px-3 py-1`}>
                    {statusConfig.label}
                  </Badge>
                </div>
              </div>
              
              {project.description && (
                <p className="text-gray-700 text-lg leading-relaxed max-w-2xl">
                  {project.description}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-white/90 hover:bg-white text-gray-800 shadow-lg border-0">
                    <statusConfig.icon className="h-4 w-4 mr-2" />
                    Alterar Status
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <DropdownMenuItem key={key} onClick={() => handleStatusChange(key)}>
                      <config.icon className="h-4 w-4 mr-2" />
                      {config.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to={createPageUrl(`EditProject?id=${project.id}`)}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="bg-white/90 hover:bg-white text-gray-800 shadow-lg border-0">
                    <EditIcon className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>

          {/* Progress Bar do Projeto */}
          {projectStats && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Progresso do Projeto
                </h3>
                <div className="text-2xl font-bold text-gray-800">
                  {projectStats.progress}%
                </div>
              </div>
              <Progress value={projectStats.progress} className="h-3 bg-gray-200" />
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>{projectStats.completedTasks} de {projectStats.totalTasks} tarefas concluídas</span>
                {projectStats.daysRemaining !== null && (
                  <span className={projectStats.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                    {projectStats.isOverdue 
                      ? `${Math.abs(projectStats.daysRemaining)} dias atrasado` 
                      : `${projectStats.daysRemaining} dias restantes`
                    }
                  </span>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      <div className="grid gap-8 grid-cols-1 xl:grid-cols-3">
        {/* Coluna Principal - Informações e Estatísticas */}
        <div className="xl:col-span-2 space-y-6">
          
          {/* Cards de Estatísticas */}
          {projectStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{projectStats.totalTasks}</p>
                      <p className="text-xs text-blue-600">Total Tarefas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{projectStats.completedTasks}</p>
                      <p className="text-xs text-green-600">Concluídas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-700">{projectStats.highPriorityTasks}</p>
                      <p className="text-xs text-orange-600">Alta Prioridade</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg">
                      <Timer className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-700">{projectStats.inProgressTasks}</p>
                      <p className="text-xs text-purple-600">Em Andamento</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Detalhes do Projeto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-6">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <CalendarIcon className="w-6 h-6 text-primary" />
                  Cronograma do Projeto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl border border-blue-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-r from-blue-500 to-sky-600 rounded-lg">
                        <Calendar className="h-4 w-4 text-white" />
                      </div>
                      <h4 className="font-semibold text-blue-700">Data de Início</h4>
                    </div>
                    <p className="text-lg font-medium text-gray-800">
                      {project.start_date ? format(new Date(project.start_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Não definida'}
                    </p>
                  </motion.div>
                  
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                        <Target className="h-4 w-4 text-white" />
                      </div>
                      <h4 className="font-semibold text-green-700">Data de Término</h4>
                    </div>
                    <p className="text-lg font-medium text-gray-800">
                      {project.end_date ? format(new Date(project.end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Não definida'}
                    </p>
                  </motion.div>
                </div>

                {/* Timeline Visual */}
                {project.start_date && project.end_date && projectStats && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border"
                  >
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <LineChart className="w-5 h-5" />
                      Timeline do Projeto
                    </h4>
                    <div className="relative">
                      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-green-500 rounded-full"></div>
                      <div className="pl-8 space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="w-3 h-3 bg-blue-500 rounded-full -ml-8 relative z-10 ring-4 ring-white shadow-md"></div>
                          <div>
                            <p className="font-medium text-gray-800">Início do Projeto</p>
                            <p className="text-sm text-gray-600">{format(new Date(project.start_date), "dd/MM/yyyy")}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full -ml-8 relative z-10 ring-4 ring-white shadow-md"></div>
                          <div>
                            <p className="font-medium text-gray-800">Situação Atual</p>
                            <p className="text-sm text-gray-600">{projectStats.progress}% concluído</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-3 h-3 bg-green-500 rounded-full -ml-8 relative z-10 ring-4 ring-white shadow-md"></div>
                          <div>
                            <p className="font-medium text-gray-800">Entrega Prevista</p>
                            <p className="text-sm text-gray-600">{format(new Date(project.end_date), "dd/MM/yyyy")}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Distribuição de Tarefas */}
          {projectStats && projectStats.totalTasks > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <PieChart className="w-6 h-6 text-primary" />
                    Distribuição de Tarefas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="text-center p-4 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl border"
                    >
                      <div className="text-2xl font-bold text-slate-700">{projectStats.todoTasks}</div>
                      <div className="text-sm text-slate-600">A Fazer</div>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-gradient-to-r from-slate-500 to-gray-600 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${projectStats.totalTasks > 0 ? (projectStats.todoTasks / projectStats.totalTasks) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="text-center p-4 bg-gradient-to-br from-blue-50 to-sky-100 rounded-xl border border-blue-200"
                    >
                      <div className="text-2xl font-bold text-blue-700">{projectStats.inProgressTasks}</div>
                      <div className="text-sm text-blue-600">Em Andamento</div>
                      <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-sky-600 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${projectStats.totalTasks > 0 ? (projectStats.inProgressTasks / projectStats.totalTasks) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl border border-amber-200"
                    >
                      <div className="text-2xl font-bold text-amber-700">{projectStats.reviewTasks}</div>
                      <div className="text-sm text-amber-600">Revisão</div>
                      <div className="w-full bg-amber-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-gradient-to-r from-amber-500 to-orange-600 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${projectStats.totalTasks > 0 ? (projectStats.reviewTasks / projectStats.totalTasks) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border border-green-200"
                    >
                      <div className="text-2xl font-bold text-green-700">{projectStats.completedTasks}</div>
                      <div className="text-sm text-green-600">Concluídas</div>
                      <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${projectStats.totalTasks > 0 ? (projectStats.completedTasks / projectStats.totalTasks) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </motion.div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Sidebar - Cliente, Gestor e Equipe */}
        <div className="space-y-6">
          {/* Cliente */}
          {company && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-primary" />
                    Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl border border-blue-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-gradient-to-r from-blue-500 to-sky-600 rounded-xl shadow-lg">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800 text-lg mb-1">
                          {company.name || company.corporate_name}
                        </h3>
                        {company.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <Mail className="w-4 h-4" />
                            {company.email}
                          </div>
                        )}
                        {company.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <Phone className="w-4 h-4" />
                            {company.phone}
                          </div>
                        )}
                        {company.website && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Globe className="w-4 h-4" />
                            {company.website}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Gestor do Projeto */}
          {manager && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="w-5 h-5 text-primary" />
                    Gestor do Projeto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200"
                  >
                    <div className="flex items-center gap-4">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="relative"
                      >
                        <Avatar className="h-16 w-16 ring-4 ring-white shadow-lg">
                          <AvatarImage src={manager.avatar_url} />
                          <AvatarFallback className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-lg font-bold">
                            {getTeamMemberInitials(manager)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full ring-2 ring-white"></div>
                      </motion.div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800 text-lg">
                          {manager.name || manager.full_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">{manager.email}</p>
                        <Badge className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 border-purple-200">
                          <Briefcase className="w-3 h-3 mr-1" />
                          Gestor
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          )}
          
          {/* Equipe do Projeto */}
          {teamMembers && teamMembers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Equipe
                    </div>
                    <Badge className="bg-gradient-to-r from-blue-100 to-sky-100 text-blue-700 border-blue-200">
                      {teamMembers.length} membros
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <AnimatePresence>
                    {teamMembers.map((member, index) => (
                      <motion.div
                        key={member.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="h-10 w-10 ring-2 ring-white shadow-md">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-sky-600 text-white font-semibold">
                                {getTeamMemberInitials(member)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white"></div>
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800 text-sm">
                              {member.name || member.full_name}
                            </p>
                            <p className="text-xs text-gray-600">{member.email}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {member.role === 'admin' ? 'Admin' :
                             member.role === 'support' ? 'Suporte' :
                             member.role === 'developer' ? 'Dev' : 'Membro'}
                          </Badge>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Alertas e Notificações */}
          {projectStats && (projectStats.overdueTasks > 0 || projectStats.isOverdue) && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card className="shadow-xl border-0 bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Alertas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {projectStats.isOverdue && (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="p-3 bg-gradient-to-r from-red-100 to-red-200 rounded-xl border border-red-300"
                    >
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <div>
                          <p className="font-semibold text-red-700">Projeto Atrasado</p>
                          <p className="text-sm text-red-600">
                            {Math.abs(projectStats.daysRemaining)} dias além do prazo
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  {projectStats.overdueTasks > 0 && (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="p-3 bg-gradient-to-r from-orange-100 to-yellow-200 rounded-xl border border-orange-300"
                    >
                      <div className="flex items-center gap-3">
                        <Timer className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="font-semibold text-orange-700">Tarefas Atrasadas</p>
                          <p className="text-sm text-orange-600">
                            {projectStats.overdueTasks} tarefa{projectStats.overdueTasks > 1 ? 's' : ''} vencida{projectStats.overdueTasks > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
